// src/types/meeting.ts
var MINUTES_EMPTY_TRANSCRIPT = "empty_transcript";

// src/types/native-transcription.ts
var NATIVE_TRANSCRIPTION_PROVIDERS = [
  "MICROSOFT_TEAMS",
  "GOOGLE_MEET"
];
var NATIVE_SESSION_STATUSES = [
  "CREATED",
  "STARTING",
  "WAITING_CONSENT",
  "CONNECTED",
  "RECONNECTING",
  "STOPPING",
  "COMPLETED",
  "FAILED",
  "REVOKED"
];

// src/types/ai.ts
var EMPTY_PREVIOUS_ANALYSIS = {
  main_content: [],
  key_points: [],
  summary: "",
  actions: []
};
var EMPTY_MEETING_MINUTES = {
  purpose: [],
  topics: [],
  action_items: []
};

// src/types/internal-meeting.ts
function isMinutesJobSettled(status) {
  return status === "succeeded" || status === "failed";
}

// src/types/room-event.ts
var ROOM_EVENT_TYPES = [
  "transcript.final",
  "translation.final",
  "translation.batch",
  "translation.warning",
  "summarize.result",
  "main-content.result",
  "key-points.result",
  "confirm-items.result",
  "meeting-minutes.result",
  "main-content.updated",
  "language.updated",
  "assist.card"
];
function isRoomEventType(type) {
  return typeof type === "string" && ROOM_EVENT_TYPES.includes(type);
}
var ROOM_EVENT_BATCH_LIMIT = 200;

// src/types/glossary.ts
var GLOSSARY_CSV_HEADERS = {
  term: "\u8A00\u8A9E\uFF11\uFF08\u65E5\u672C\u8A9E\uFF09",
  meaning: "\u8A00\u8A9E\uFF12\uFF08\u30D9\u30C8\u30CA\u30E0\u8A9E\u30FB\u82F1\u8A9E\uFF09"
};
var GLOSSARY_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
var GLOSSARY_UPLOAD_MAX_ROWS = 2e3;
var GLOSSARY_UPLOAD_MAX_CELL_LENGTH = 100;

// src/types/user-activity.ts
var USER_ACTIVITY_ACTIONS = [
  // The meeting itself.
  "meeting.kind_selected",
  "meeting.started",
  "meeting.ended",
  // The explicit answer to the same-meeting prompt. Keep Yes and No apart:
  // declining a room is a valid choice, not a failed acceptance.
  "shared_room.join_accepted",
  "shared_room.join_declined",
  // The live screen's five collapsible panels, recorded once per meeting when
  // it is ended rather than once per click. Which panels start open is decided
  // by the Mode, not by the reader, so counting clicks would report zero uses
  // of a panel everybody reads all meeting and never touches.
  "panel.input_in_use",
  "panel.output_in_use",
  "panel.enhanced_in_use",
  "panel.live_memo_in_use",
  "panel.key_points_in_use",
  // Copilot. `question_asked` is one the reader typed; `suggestion_asked` is
  // one the AI offered and they pressed — the difference is the whole point of
  // counting them separately.
  "copilot.opened",
  "copilot.closed",
  "copilot.question_asked",
  "copilot.suggestion_asked",
  "copilot.project_chosen",
  "copilot.backlog_connect_opened",
  // Meeting minutes.
  "minutes.pushed_to_backlog",
  "minutes.shared_by_email",
  "minutes.shared_with_members",
  "minutes.exported_docx",
  "minutes.saved",
  // Connecting a Backlog account, from either of the two places that offer it.
  "backlog.connect_started",
  "backlog.connect_started_in_copilot",
  // Glossary.
  "glossary.uploaded",
  "glossary.updated",
  "glossary.deleted",
  "glossary.downloaded",
  // The Teams app.
  "teams.panel_opened",
  "teams.meeting_created",
  "teams.panel_retried"
];
var USER_ACTIVITY_GROUP_LABELS = {
  meeting: "Meeting",
  panel: "Live panels",
  copilot: "Copilot",
  minutes: "Minutes",
  backlog: "Backlog",
  glossary: "Glossary",
  teams: "Microsoft Teams",
  shared_room: "Shared room"
};
function userActivityGroup(action) {
  return action.slice(0, action.indexOf("."));
}
var USER_ACTIVITY_INFO = {
  "meeting.kind_selected": {
    label: "Meeting type picked",
    description: "Someone chose a meeting type on the create form. Counts reaching for the picker, not what the meeting ended up running as \u2014 that is on the meeting row itself."
  },
  "meeting.started": {
    label: "Meeting started",
    description: "Start was pressed on the live screen. A start that then failed is counted as a failure rather than left out."
  },
  "meeting.ended": {
    label: "Meeting ended",
    description: "End was pressed. Recorded on the way in, so the long tail of saving and uploading afterwards cannot turn a finished meeting into a failed one."
  },
  "shared_room.join_accepted": {
    label: "Shared-room join accepted",
    description: "Yes was selected on the same-meeting prompt. A failure means the decision could not be sent to the active room offer."
  },
  "shared_room.join_declined": {
    label: "Shared-room join declined",
    description: "No was selected on the same-meeting prompt. Closing or cancelling the prompt is not counted as a No."
  },
  "panel.input_in_use": {
    label: "Input pane open at the end",
    description: "The source-language transcript was still open when the meeting was ended. One per meeting, not per click: a pane left at the Mode\u2019s default counts too, which is the only way a pane nobody has to touch shows up at all."
  },
  "panel.output_in_use": {
    label: "Output pane open at the end",
    description: "The translated transcript was still open when the meeting was ended."
  },
  "panel.enhanced_in_use": {
    label: "Enhanced mode open at the end",
    description: "The cleaned-up rendering was still open at the end. It starts closed in every Mode but Presentation, so a count here is mostly people who went and opened it."
  },
  "panel.live_memo_in_use": {
    label: "Live memo open at the end",
    description: "The rolling summary was still open when the meeting was ended."
  },
  "panel.key_points_in_use": {
    label: "Key points open at the end",
    description: "The key-points pane was still open when the meeting was ended."
  },
  "copilot.opened": {
    label: "Copilot opened",
    description: "The assistant panel was opened from the live meeting screen."
  },
  "copilot.closed": {
    label: "Copilot closed",
    description: "The assistant panel was closed again."
  },
  "copilot.question_asked": {
    label: "Question typed",
    description: "A question the reader wrote themselves. Counted apart from the offered ones because the difference is the point: one is a person with something to ask."
  },
  "copilot.suggestion_asked": {
    label: "Suggested question used",
    description: "One of the questions the assistant offered was pressed instead of typed \u2014 how often the suggestions are worth making."
  },
  "copilot.project_chosen": {
    label: "Project chosen in Copilot",
    description: "A Backlog project was picked, or changed, from inside the assistant panel without restarting the meeting."
  },
  "copilot.backlog_connect_opened": {
    label: "Connect dialog opened in Copilot",
    description: "The assistant offered to connect Backlog and the dialog was opened. Opening it is not connecting \u2014 compare with the Backlog rows below."
  },
  "minutes.pushed_to_backlog": {
    label: "Minutes pushed to Backlog",
    description: "Minutes were sent to Backlog as issues."
  },
  "minutes.shared_by_email": {
    label: "Minutes emailed",
    description: "Minutes were sent to an email address from the meeting detail screen."
  },
  "minutes.shared_with_members": {
    label: "Minutes shared with members",
    description: "Minutes were shared with other people in the workspace, rather than by email."
  },
  "minutes.exported_docx": {
    label: "Minutes exported as .docx",
    description: "Minutes were downloaded as a Word document."
  },
  "minutes.saved": {
    label: "Minutes saved",
    description: "Edited minutes were saved."
  },
  "backlog.connect_started": {
    label: "Backlog connect started (Settings)",
    description: "A Backlog account connection was started from the Settings screen. A failure here means the sign-in could not even be started."
  },
  "backlog.connect_started_in_copilot": {
    label: "Backlog connect started (Copilot)",
    description: "The same connection, begun from the dialog inside the assistant panel instead of from Settings."
  },
  "glossary.uploaded": {
    label: "Glossary uploaded",
    description: "A dictionary file was uploaded."
  },
  "glossary.updated": {
    label: "Glossary updated",
    description: "An existing dictionary was edited."
  },
  "glossary.deleted": {
    label: "Glossary deleted",
    description: "A dictionary was removed."
  },
  "glossary.downloaded": {
    label: "Glossary downloaded",
    description: "A dictionary was downloaded back out."
  },
  "teams.panel_opened": {
    label: "Teams panel opened",
    description: "The app panel became usable inside a Teams call. The tab opened from the meeting chat is deliberately not counted \u2014 nothing can be started from it."
  },
  "teams.meeting_created": {
    label: "Start pressed in Teams",
    description: "Start transcription was pressed from inside the Teams panel."
  },
  "teams.panel_retried": {
    label: "Teams panel retried",
    description: "Retry was pressed after the panel failed to connect \u2014 a count of how often the Teams sign-in needs a second go."
  }
};

// src/constants/languages.ts
var LANGUAGES = [
  { code: "ja", label: "\u65E5\u672C\u8A9E", flag: "\u{1F1EF}\u{1F1F5}" },
  { code: "vi", label: "Ti\u1EBFng Vi\u1EC7t", flag: "\u{1F1FB}\u{1F1F3}" },
  { code: "en", label: "English", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "zh", label: "\u4E2D\u6587", flag: "\u{1F1E8}\u{1F1F3}" },
  { code: "ko", label: "\uD55C\uAD6D\uC5B4", flag: "\u{1F1F0}\u{1F1F7}" },
  { code: "fr", label: "Fran\xE7ais", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "de", label: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "es", label: "Espa\xF1ol", flag: "\u{1F1EA}\u{1F1F8}" }
];

// src/constants/email-domains.ts
var INTERNAL_EMAIL_DOMAINS = ["@reactplus.com", "@mirai-plus.co.jp"];
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
function isInternalEmail(email) {
  const normalized = normalizeEmail(email);
  return INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain));
}

// src/assets/meeting-context.json
var meeting_context_default = {
  terms: [
    "\u30B9\u30D7\u30EA\u30F3\u30C8",
    "\u30D0\u30C3\u30AF\u30ED\u30B0",
    "\u30C7\u30D7\u30ED\u30A4",
    "\u30D7\u30EB\u30EA\u30AF\u30A8\u30B9\u30C8",
    "\u30DE\u30FC\u30B8",
    "\u30D6\u30E9\u30F3\u30C1",
    "\u30B3\u30DF\u30C3\u30C8",
    "\u30EA\u30D5\u30A1\u30AF\u30BF\u30EA\u30F3\u30B0",
    "\u30B3\u30FC\u30C9\u30EC\u30D3\u30E5\u30FC",
    "\u30EA\u30EA\u30FC\u30B9",
    "\u30D0\u30B0",
    "\u30D5\u30EC\u30FC\u30E0\u30EF\u30FC\u30AF",
    "\u30C7\u30FC\u30BF\u30D9\u30FC\u30B9",
    "\u30A4\u30F3\u30D5\u30E9",
    "\u30B9\u30AF\u30E9\u30E0",
    "\u30A2\u30B8\u30E3\u30A4\u30EB",
    "\u30EC\u30D3\u30E5\u30FC",
    "\u30C6\u30B9\u30C8",
    "\u30B9\u30C6\u30FC\u30B8\u30F3\u30B0",
    "\u30DE\u30A4\u30AF\u30ED\u30B5\u30FC\u30D3\u30B9",
    "\u30B3\u30F3\u30C6\u30CA",
    "\u30AF\u30E9\u30A6\u30C9",
    "\u30A8\u30F3\u30C9\u30DD\u30A4\u30F3\u30C8",
    "\u30DF\u30C9\u30EB\u30A6\u30A7\u30A2",
    "\u30AD\u30E3\u30C3\u30B7\u30E5",
    "\u30AF\u30A8\u30EA",
    "\u30B9\u30AD\u30FC\u30DE",
    "\u30A4\u30F3\u30C7\u30C3\u30AF\u30B9",
    "\u30C8\u30FC\u30AF\u30F3",
    "\u30BB\u30C3\u30B7\u30E7\u30F3",
    "\u30AF\u30C3\u30AD\u30FC",
    "\u30A6\u30A7\u30D6\u30D5\u30C3\u30AF",
    "\u30AF\u30ED\u30F3",
    "\u30E9\u30A4\u30D6\u30E9\u30EA",
    "\u30D1\u30C3\u30B1\u30FC\u30B8",
    "\u30E2\u30B8\u30E5\u30FC\u30EB",
    "\u30B3\u30F3\u30DD\u30FC\u30CD\u30F3\u30C8",
    "\u30A4\u30F3\u30BF\u30FC\u30D5\u30A7\u30FC\u30B9",
    "\u30EA\u30DD\u30B8\u30C8\u30EA",
    "\u30D1\u30A4\u30D7\u30E9\u30A4\u30F3",
    "\u30C0\u30C3\u30B7\u30E5\u30DC\u30FC\u30C9",
    "\u30C7\u30D0\u30C3\u30B0",
    "\u30D0\u30EA\u30C7\u30FC\u30B7\u30E7\u30F3",
    "\u30AA\u30FC\u30BB\u30F3\u30C6\u30A3\u30B1\u30FC\u30B7\u30E7\u30F3",
    "\u30AA\u30FC\u30BD\u30E9\u30A4\u30BC\u30FC\u30B7\u30E7\u30F3",
    "\u30A2\u30FC\u30AD\u30C6\u30AF\u30C1\u30E3",
    "\u30D1\u30D5\u30A9\u30FC\u30DE\u30F3\u30B9",
    "\u30B9\u30B1\u30FC\u30E9\u30D3\u30EA\u30C6\u30A3",
    "\u30DE\u30A4\u30B0\u30EC\u30FC\u30B7\u30E7\u30F3",
    "\u30ED\u30FC\u30EB\u30D0\u30C3\u30AF",
    "\u30E2\u30CB\u30BF\u30EA\u30F3\u30B0",
    "\u30D9\u30F3\u30C1\u30DE\u30FC\u30AF",
    "\u30ED\u30FC\u30C9\u30D0\u30E9\u30F3\u30B5\u30FC",
    "\u30EC\u30FC\u30C8\u30EA\u30DF\u30C3\u30C8",
    "\u30D5\u30A3\u30FC\u30C1\u30E3\u30FC\u30D5\u30E9\u30B0",
    "\u30DB\u30C3\u30C8\u30D5\u30A3\u30C3\u30AF\u30B9",
    "\u30EA\u30B0\u30EC\u30C3\u30B7\u30E7\u30F3",
    "\u30E2\u30C3\u30AF",
    "\u30B9\u30BF\u30D6",
    "\u30ED\u30B0",
    "pull request",
    "deploy",
    "sprint",
    "backlog",
    "merge",
    "branch",
    "commit",
    "bug fix",
    "hotfix",
    "release",
    "refactoring",
    "code review",
    "CI/CD",
    "BrSE",
    "GitHub",
    "staging",
    "microservice",
    "container",
    "Docker",
    "Kubernetes",
    "AWS",
    "GCP",
    "Azure",
    "endpoint",
    "middleware",
    "cache",
    "query",
    "schema",
    "token",
    "webhook",
    "pipeline",
    "repository",
    "rollback",
    "migration",
    "monitoring",
    "benchmark",
    "mock",
    "unit test",
    "integration test",
    "regression test",
    "acceptance test",
    "load balancer",
    "rate limit",
    "feature flag",
    "A/B test",
    "API gateway",
    "GraphQL",
    "REST",
    "gRPC",
    "OAuth",
    "JWT",
    "SSL",
    "HTTPS",
    "DevOps",
    "scrum",
    "agile",
    "story point",
    "velocity",
    "retrospective",
    "tech debt",
    "build",
    "dev",
    "log",
    "\u8981\u4EF6\u5B9A\u7FA9",
    "\u8A73\u7D30\u8A2D\u8A08",
    "\u57FA\u672C\u8A2D\u8A08",
    "\u5358\u4F53\u30C6\u30B9\u30C8",
    "\u7D50\u5408\u30C6\u30B9\u30C8",
    "\u53D7\u3051\u5165\u308C\u30C6\u30B9\u30C8",
    "\u4ED5\u69D8\u66F8",
    "\u8A2D\u8A08\u66F8",
    "\u7D0D\u54C1",
    "\u4E0D\u5177\u5408",
    "\u958B\u767A\u74B0\u5883",
    "\u672C\u756A\u74B0\u5883",
    "\u8A66\u9A13\u74B0\u5883",
    "\u904B\u7528",
    "\u4FDD\u5B88",
    "\u6A5F\u80FD\u8FFD\u52A0",
    "\u6539\u4FEE",
    "\u4FEE\u6B63",
    "\u5B9F\u88C5",
    "\u62C5\u5F53\u8005",
    "\u671F\u9650",
    "\u9032\u6357",
    "\u5DE5\u6570",
    "\u898B\u7A4D\u3082\u308A",
    "\u624B\u623B\u308A",
    "\u539F\u56E0\u8ABF\u67FB",
    "\u5BFE\u5FDC\u7B56",
    "\u30A8\u30E9\u30FC\u30ED\u30B0",
    "\u30EC\u30D3\u30E5\u30FC\u6307\u6458",
    "\u30D7\u30EB\u30EA\u30AF",
    "\u30D4\u30FC\u30A2\u30FC\u30EB",
    "\u8A8D\u8A3C",
    "\u8A8D\u53EF",
    "\u30D0\u30FC\u30B8\u30E7\u30F3",
    "\u30D0\u30C3\u30C1",
    "\u30B3\u30DE\u30F3\u30C9",
    "\u30BF\u30B9\u30AF",
    "\u30C1\u30B1\u30C3\u30C8",
    "\u30B9\u30EC\u30C3\u30C9",
    "\u30A2\u30AF\u30BB\u30B9",
    "\u30A4\u30F3\u30B7\u30C7\u30F3\u30C8",
    "\u30EC\u30A4\u30C6\u30F3\u30B7\u30FC",
    "\u30B3\u30E1\u30F3\u30C8",
    "\u30B3\u30E1\u30F3\u30C8\u30A2\u30A6\u30C8",
    "\u6307\u6458",
    "\u30A4\u30D9\u30F3\u30C8",
    "\u30EA\u30CF\u30FC\u30B5\u30EB",
    "\u30BF\u30A4\u30DF\u30F3\u30B0",
    "\u30BF\u30A4\u30E0\u30A2\u30A6\u30C8",
    "\u30B9\u30AF\u30EA\u30D7\u30C8",
    "\u30A8\u30D3\u30C7\u30F3\u30B9",
    "\u7DCF\u5408\u30C6\u30B9\u30C8",
    "\u6027\u80FD\u30C6\u30B9\u30C8",
    "\u4ED5\u69D8",
    "\u672C\u756A",
    "\u5BFE\u5FDC",
    "\u5B9F\u884C",
    "\u4EBA\u6708",
    "\u4EBA\u65E5",
    "\u4EBA\u6642",
    "\u627F\u8A8D",
    "\u8CA0\u8377",
    "\u64CD\u4F5C",
    "\u30DC\u30BF\u30F3",
    "\u9077\u79FB",
    "\u30C0\u30A4\u30A2\u30ED\u30B0",
    "\u9023\u643A",
    "\u5BFE\u8C61",
    "\u5BFE\u8C61\u5916",
    "fix",
    "run",
    "lint",
    "map",
    "tag",
    "hash",
    "push",
    "pull",
    "clone",
    "fork",
    "pipe",
    "hook",
    "seed",
    "dump",
    "bind",
    "wrap",
    "cast",
    "load",
    "save",
    "send",
    "fetch",
    "post",
    "get",
    "put",
    "patch",
    "diff",
    "grep",
    "curl",
    "ping",
    "loop",
    "sort",
    "parse",
    "render",
    "init",
    "sync",
    "null",
    "void",
    "key",
    "scope",
    "queue",
    "stack",
    "heap",
    "blob",
    "flag",
    "enum",
    "trait",
    "type",
    "mode",
    "async",
    "await",
    "CI",
    "CD",
    "PR",
    "DB",
    "UI",
    "UX",
    "QA",
    "UAT",
    "MVP",
    "POC",
    "SDK",
    "ORM",
    "CDN",
    "DNS",
    "VPN",
    "SSH"
  ],
  translationTerms: [
    {
      source: "\u30B9\u30D7\u30EA\u30F3\u30C8",
      target: "sprint"
    },
    {
      source: "\u30D0\u30C3\u30AF\u30ED\u30B0",
      target: "backlog"
    },
    {
      source: "\u30C7\u30D7\u30ED\u30A4",
      target: "deploy"
    },
    {
      source: "\u30D7\u30EB\u30EA\u30AF\u30A8\u30B9\u30C8",
      target: "pull request"
    },
    {
      source: "\u30DE\u30FC\u30B8",
      target: "merge"
    },
    {
      source: "\u30D6\u30E9\u30F3\u30C1",
      target: "branch"
    },
    {
      source: "\u30B3\u30DF\u30C3\u30C8",
      target: "commit"
    },
    {
      source: "\u30EA\u30D5\u30A1\u30AF\u30BF\u30EA\u30F3\u30B0",
      target: "refactoring"
    },
    {
      source: "\u30B3\u30FC\u30C9\u30EC\u30D3\u30E5\u30FC",
      target: "code review"
    },
    {
      source: "\u30EA\u30EA\u30FC\u30B9",
      target: "release"
    },
    {
      source: "\u30D0\u30B0",
      target: "bug"
    },
    {
      source: "\u30D5\u30EC\u30FC\u30E0\u30EF\u30FC\u30AF",
      target: "framework"
    },
    {
      source: "\u30C7\u30FC\u30BF\u30D9\u30FC\u30B9",
      target: "database"
    },
    {
      source: "\u30A4\u30F3\u30D5\u30E9",
      target: "infrastructure"
    },
    {
      source: "\u30B9\u30AF\u30E9\u30E0",
      target: "scrum"
    },
    {
      source: "\u30A2\u30B8\u30E3\u30A4\u30EB",
      target: "agile"
    },
    {
      source: "\u30EC\u30D3\u30E5\u30FC",
      target: "review"
    },
    {
      source: "\u30C6\u30B9\u30C8",
      target: "test"
    },
    {
      source: "\u30B9\u30C6\u30FC\u30B8\u30F3\u30B0",
      target: "staging"
    },
    {
      source: "\u30DE\u30A4\u30AF\u30ED\u30B5\u30FC\u30D3\u30B9",
      target: "microservice"
    },
    {
      source: "\u30B3\u30F3\u30C6\u30CA",
      target: "container"
    },
    {
      source: "\u30AF\u30E9\u30A6\u30C9",
      target: "cloud"
    },
    {
      source: "\u30A8\u30F3\u30C9\u30DD\u30A4\u30F3\u30C8",
      target: "endpoint"
    },
    {
      source: "\u30DF\u30C9\u30EB\u30A6\u30A7\u30A2",
      target: "middleware"
    },
    {
      source: "\u30AD\u30E3\u30C3\u30B7\u30E5",
      target: "cache"
    },
    {
      source: "\u30AF\u30A8\u30EA",
      target: "query"
    },
    {
      source: "\u30B9\u30AD\u30FC\u30DE",
      target: "schema"
    },
    {
      source: "\u30A4\u30F3\u30C7\u30C3\u30AF\u30B9",
      target: "index"
    },
    {
      source: "\u30C8\u30FC\u30AF\u30F3",
      target: "token"
    },
    {
      source: "\u30BB\u30C3\u30B7\u30E7\u30F3",
      target: "session"
    },
    {
      source: "\u30AF\u30C3\u30AD\u30FC",
      target: "cookie"
    },
    {
      source: "\u30A6\u30A7\u30D6\u30D5\u30C3\u30AF",
      target: "webhook"
    },
    {
      source: "\u30AF\u30ED\u30F3",
      target: "cron"
    },
    {
      source: "\u30E9\u30A4\u30D6\u30E9\u30EA",
      target: "library"
    },
    {
      source: "\u30D1\u30C3\u30B1\u30FC\u30B8",
      target: "package"
    },
    {
      source: "\u30E2\u30B8\u30E5\u30FC\u30EB",
      target: "module"
    },
    {
      source: "\u30B3\u30F3\u30DD\u30FC\u30CD\u30F3\u30C8",
      target: "component"
    },
    {
      source: "\u30A4\u30F3\u30BF\u30FC\u30D5\u30A7\u30FC\u30B9",
      target: "interface"
    },
    {
      source: "\u30EA\u30DD\u30B8\u30C8\u30EA",
      target: "repository"
    },
    {
      source: "\u30D1\u30A4\u30D7\u30E9\u30A4\u30F3",
      target: "pipeline"
    },
    {
      source: "\u30C0\u30C3\u30B7\u30E5\u30DC\u30FC\u30C9",
      target: "dashboard"
    },
    {
      source: "\u30C7\u30D0\u30C3\u30B0",
      target: "debug"
    },
    {
      source: "\u30D0\u30EA\u30C7\u30FC\u30B7\u30E7\u30F3",
      target: "validation"
    },
    {
      source: "\u30AA\u30FC\u30BB\u30F3\u30C6\u30A3\u30B1\u30FC\u30B7\u30E7\u30F3",
      target: "authentication"
    },
    {
      source: "\u30AA\u30FC\u30BD\u30E9\u30A4\u30BC\u30FC\u30B7\u30E7\u30F3",
      target: "authorization"
    },
    {
      source: "\u30A2\u30FC\u30AD\u30C6\u30AF\u30C1\u30E3",
      target: "architecture"
    },
    {
      source: "\u30D1\u30D5\u30A9\u30FC\u30DE\u30F3\u30B9",
      target: "performance"
    },
    {
      source: "\u30B9\u30B1\u30FC\u30E9\u30D3\u30EA\u30C6\u30A3",
      target: "scalability"
    },
    {
      source: "\u30DE\u30A4\u30B0\u30EC\u30FC\u30B7\u30E7\u30F3",
      target: "migration"
    },
    {
      source: "\u30ED\u30FC\u30EB\u30D0\u30C3\u30AF",
      target: "rollback"
    },
    {
      source: "\u30E2\u30CB\u30BF\u30EA\u30F3\u30B0",
      target: "monitoring"
    },
    {
      source: "\u30D9\u30F3\u30C1\u30DE\u30FC\u30AF",
      target: "benchmark"
    },
    {
      source: "\u30ED\u30FC\u30C9\u30D0\u30E9\u30F3\u30B5\u30FC",
      target: "load balancer"
    },
    {
      source: "\u30EC\u30FC\u30C8\u30EA\u30DF\u30C3\u30C8",
      target: "rate limit"
    },
    {
      source: "\u30D5\u30A3\u30FC\u30C1\u30E3\u30FC\u30D5\u30E9\u30B0",
      target: "feature flag"
    },
    {
      source: "\u30DB\u30C3\u30C8\u30D5\u30A3\u30C3\u30AF\u30B9",
      target: "hotfix"
    },
    {
      source: "\u30EA\u30B0\u30EC\u30C3\u30B7\u30E7\u30F3",
      target: "regression"
    },
    {
      source: "\u30E2\u30C3\u30AF",
      target: "mock"
    },
    {
      source: "\u30B9\u30BF\u30D6",
      target: "stub"
    },
    {
      source: "\u30ED\u30B0",
      target: "log"
    },
    {
      source: "pull request",
      target: "pull request"
    },
    {
      source: "deploy",
      target: "deploy"
    },
    {
      source: "sprint",
      target: "sprint"
    },
    {
      source: "backlog",
      target: "backlog"
    },
    {
      source: "merge",
      target: "merge"
    },
    {
      source: "branch",
      target: "branch"
    },
    {
      source: "commit",
      target: "commit"
    },
    {
      source: "bug fix",
      target: "bug fix"
    },
    {
      source: "hotfix",
      target: "hotfix"
    },
    {
      source: "release",
      target: "release"
    },
    {
      source: "refactoring",
      target: "refactoring"
    },
    {
      source: "code review",
      target: "code review"
    },
    {
      source: "CI/CD",
      target: "CI/CD"
    },
    {
      source: "BrSE",
      target: "BrSE"
    },
    {
      source: "GitHub",
      target: "GitHub"
    },
    {
      source: "staging",
      target: "staging"
    },
    {
      source: "microservice",
      target: "microservice"
    },
    {
      source: "container",
      target: "container"
    },
    {
      source: "Docker",
      target: "Docker"
    },
    {
      source: "Kubernetes",
      target: "Kubernetes"
    },
    {
      source: "AWS",
      target: "AWS"
    },
    {
      source: "GCP",
      target: "GCP"
    },
    {
      source: "Azure",
      target: "Azure"
    },
    {
      source: "endpoint",
      target: "endpoint"
    },
    {
      source: "middleware",
      target: "middleware"
    },
    {
      source: "cache",
      target: "cache"
    },
    {
      source: "query",
      target: "query"
    },
    {
      source: "schema",
      target: "schema"
    },
    {
      source: "token",
      target: "token"
    },
    {
      source: "webhook",
      target: "webhook"
    },
    {
      source: "pipeline",
      target: "pipeline"
    },
    {
      source: "repository",
      target: "repository"
    },
    {
      source: "rollback",
      target: "rollback"
    },
    {
      source: "migration",
      target: "migration"
    },
    {
      source: "monitoring",
      target: "monitoring"
    },
    {
      source: "benchmark",
      target: "benchmark"
    },
    {
      source: "mock",
      target: "mock"
    },
    {
      source: "unit test",
      target: "unit test"
    },
    {
      source: "integration test",
      target: "integration test"
    },
    {
      source: "regression test",
      target: "regression test"
    },
    {
      source: "acceptance test",
      target: "acceptance test"
    },
    {
      source: "load balancer",
      target: "load balancer"
    },
    {
      source: "rate limit",
      target: "rate limit"
    },
    {
      source: "feature flag",
      target: "feature flag"
    },
    {
      source: "A/B test",
      target: "A/B test"
    },
    {
      source: "API gateway",
      target: "API gateway"
    },
    {
      source: "GraphQL",
      target: "GraphQL"
    },
    {
      source: "REST",
      target: "REST"
    },
    {
      source: "gRPC",
      target: "gRPC"
    },
    {
      source: "OAuth",
      target: "OAuth"
    },
    {
      source: "JWT",
      target: "JWT"
    },
    {
      source: "SSL",
      target: "SSL"
    },
    {
      source: "HTTPS",
      target: "HTTPS"
    },
    {
      source: "DevOps",
      target: "DevOps"
    },
    {
      source: "scrum",
      target: "scrum"
    },
    {
      source: "agile",
      target: "agile"
    },
    {
      source: "story point",
      target: "story point"
    },
    {
      source: "velocity",
      target: "velocity"
    },
    {
      source: "retrospective",
      target: "retrospective"
    },
    {
      source: "tech debt",
      target: "tech debt"
    },
    {
      source: "build",
      target: "build"
    },
    {
      source: "dev",
      target: "dev"
    },
    {
      source: "log",
      target: "log"
    },
    {
      source: "\u8981\u4EF6\u5B9A\u7FA9",
      target: "\u0111\u1ECBnh ngh\u0129a y\xEAu c\u1EA7u"
    },
    {
      source: "\u8A73\u7D30\u8A2D\u8A08",
      target: "thi\u1EBFt k\u1EBF chi ti\u1EBFt"
    },
    {
      source: "\u57FA\u672C\u8A2D\u8A08",
      target: "thi\u1EBFt k\u1EBF c\u01A1 b\u1EA3n"
    },
    {
      source: "\u5358\u4F53\u30C6\u30B9\u30C8",
      target: "unit test"
    },
    {
      source: "\u7D50\u5408\u30C6\u30B9\u30C8",
      target: "integration test"
    },
    {
      source: "\u53D7\u3051\u5165\u308C\u30C6\u30B9\u30C8",
      target: "acceptance test"
    },
    {
      source: "\u4ED5\u69D8\u66F8",
      target: "t\xE0i li\u1EC7u \u0111\u1EB7c t\u1EA3"
    },
    {
      source: "\u8A2D\u8A08\u66F8",
      target: "t\xE0i li\u1EC7u thi\u1EBFt k\u1EBF"
    },
    {
      source: "\u7D0D\u54C1",
      target: "b\xE0n giao"
    },
    {
      source: "\u4E0D\u5177\u5408",
      target: "l\u1ED7i / bug"
    },
    {
      source: "\u958B\u767A\u74B0\u5883",
      target: "m\xF4i tr\u01B0\u1EDDng ph\xE1t tri\u1EC3n"
    },
    {
      source: "\u672C\u756A\u74B0\u5883",
      target: "m\xF4i tr\u01B0\u1EDDng production"
    },
    {
      source: "\u8A66\u9A13\u74B0\u5883",
      target: "m\xF4i tr\u01B0\u1EDDng ki\u1EC3m th\u1EED"
    },
    {
      source: "\u904B\u7528",
      target: "v\u1EADn h\xE0nh"
    },
    {
      source: "\u4FDD\u5B88",
      target: "b\u1EA3o tr\xEC"
    },
    {
      source: "\u6A5F\u80FD\u8FFD\u52A0",
      target: "th\xEAm t\xEDnh n\u0103ng"
    },
    {
      source: "\u6539\u4FEE",
      target: "ch\u1EC9nh s\u1EEDa"
    },
    {
      source: "\u4FEE\u6B63",
      target: "s\u1EEDa l\u1ED7i"
    },
    {
      source: "\u5B9F\u88C5",
      target: "tri\u1EC3n khai / implement"
    },
    {
      source: "\u62C5\u5F53\u8005",
      target: "ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch"
    },
    {
      source: "\u671F\u9650",
      target: "deadline"
    },
    {
      source: "\u9032\u6357",
      target: "ti\u1EBFn \u0111\u1ED9"
    },
    {
      source: "\u5DE5\u6570",
      target: "c\xF4ng s\u1ED1"
    },
    {
      source: "\u898B\u7A4D\u3082\u308A",
      target: "\u01B0\u1EDBc t\xEDnh"
    },
    {
      source: "\u624B\u623B\u308A",
      target: "l\xE0m l\u1EA1i"
    },
    {
      source: "\u539F\u56E0\u8ABF\u67FB",
      target: "\u0111i\u1EC1u tra nguy\xEAn nh\xE2n"
    },
    {
      source: "\u5BFE\u5FDC\u7B56",
      target: "gi\u1EA3i ph\xE1p x\u1EED l\xFD"
    },
    {
      source: "\u30A8\u30E9\u30FC\u30ED\u30B0",
      target: "error log"
    },
    {
      source: "\u30EC\u30D3\u30E5\u30FC\u6307\u6458",
      target: "\xFD ki\u1EBFn review"
    },
    {
      source: "\u30D7\u30EB\u30EA\u30AF",
      target: "pull request"
    },
    {
      source: "\u30D4\u30FC\u30A2\u30FC\u30EB",
      target: "PR"
    },
    {
      source: "\u8A8D\u8A3C",
      target: "authentication"
    },
    {
      source: "\u8A8D\u53EF",
      target: "authorization"
    },
    {
      source: "\u30D0\u30FC\u30B8\u30E7\u30F3",
      target: "version"
    },
    {
      source: "\u30D0\u30C3\u30C1",
      target: "batch"
    },
    {
      source: "\u30B3\u30DE\u30F3\u30C9",
      target: "command"
    },
    {
      source: "\u30BF\u30B9\u30AF",
      target: "task"
    },
    {
      source: "\u30C1\u30B1\u30C3\u30C8",
      target: "ticket"
    },
    {
      source: "\u30B9\u30EC\u30C3\u30C9",
      target: "thread"
    },
    {
      source: "\u30A2\u30AF\u30BB\u30B9",
      target: "access"
    },
    {
      source: "\u30A4\u30F3\u30B7\u30C7\u30F3\u30C8",
      target: "s\u1EF1 c\u1ED1"
    },
    {
      source: "\u30EC\u30A4\u30C6\u30F3\u30B7\u30FC",
      target: "latency/ \u0111\u1ED9 tr\u1EC5"
    },
    {
      source: "\u30B3\u30E1\u30F3\u30C8",
      target: "comment"
    },
    {
      source: "\u30B3\u30E1\u30F3\u30C8\u30A2\u30A6\u30C8",
      target: "comment out"
    },
    {
      source: "\u6307\u6458",
      target: "comment"
    },
    {
      source: "\u30A4\u30D9\u30F3\u30C8",
      target: "event"
    },
    {
      source: "\u30EA\u30CF\u30FC\u30B5\u30EB",
      target: "rehearsal/ di\u1EC5n t\u1EADp"
    },
    {
      source: "\u30BF\u30A4\u30DF\u30F3\u30B0",
      target: "timing"
    },
    {
      source: "\u30BF\u30A4\u30E0\u30A2\u30A6\u30C8",
      target: "time out"
    },
    {
      source: "\u30B9\u30AF\u30EA\u30D7\u30C8",
      target: "script"
    },
    {
      source: "\u30A8\u30D3\u30C7\u30F3\u30B9",
      target: "evidence"
    },
    {
      source: "\u7DCF\u5408\u30C6\u30B9\u30C8",
      target: "test t\u1ED5ng h\u1EE3p"
    },
    {
      source: "\u6027\u80FD\u30C6\u30B9\u30C8",
      target: "test hi\u1EC7u n\u0103ng"
    },
    {
      source: "\u4ED5\u69D8",
      target: "spec"
    },
    {
      source: "\u672C\u756A",
      target: "m\xF4i tr\u01B0\u1EDDng production"
    },
    {
      source: "\u5BFE\u5FDC",
      target: "x\u1EED l\xFD"
    },
    {
      source: "\u5B9F\u884C",
      target: "th\u1EF1c thi"
    },
    {
      source: "\u4EBA\u6708",
      target: "man-month"
    },
    {
      source: "\u4EBA\u65E5",
      target: "man-day"
    },
    {
      source: "\u4EBA\u6642",
      target: "man-hour"
    },
    {
      source: "\u627F\u8A8D",
      target: "approve"
    },
    {
      source: "\u8CA0\u8377",
      target: "ch\u1ECBu t\u1EA3i"
    },
    {
      source: "\u64CD\u4F5C",
      target: "thao t\xE1c"
    },
    {
      source: "\u30DC\u30BF\u30F3",
      target: "button"
    },
    {
      source: "\u9077\u79FB",
      target: "di chuy\u1EC3n"
    },
    {
      source: "\u30C0\u30A4\u30A2\u30ED\u30B0",
      target: "dialog"
    },
    {
      source: "\u9023\u643A",
      target: "li\xEAn k\u1EBFt"
    },
    {
      source: "\u5BFE\u8C61",
      target: "\u0111\u1ED1i t\u01B0\u1EE3ng"
    },
    {
      source: "\u5BFE\u8C61\u5916",
      target: "n\u1EB1m ngo\xE0i \u0111\u1ED1i t\u01B0\u1EE3ng"
    }
  ],
  general: [
    {
      key: "domain",
      value: "Software engineering"
    },
    {
      key: "setting",
      value: "Technical meeting"
    },
    {
      key: "topic",
      value: "Software development"
    },
    {
      key: "language",
      value: "Japanese and Vietnamese with English technical terms"
    }
  ]
};

// src/meeting-context.ts
var SYSTEM_IT_DICTIONARY_NAME = "IT dictionary";
var MEETING_CONTEXT = meeting_context_default;
function getSystemItGlossaryTerms() {
  return MEETING_CONTEXT.translationTerms.map((entry) => ({
    term: entry.source,
    meaning: entry.target
  }));
}

// src/utils/meeting-minutes.ts
function toStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => typeof item === "string" ? item.trim() : String(item ?? "").trim()).filter(Boolean);
}
function readString(record, ...keys) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value.trim();
  }
  return "";
}
function normalizeTopic(item) {
  if (!item || typeof item !== "object") return null;
  const record = item;
  const title = readString(record, "title", "topic", "name");
  const overview = readString(record, "overview", "summary", "description");
  const discussions = toStringList(record.discussions ?? record.discussion ?? record.points);
  const conclusions = toStringList(record.conclusions ?? record.conclusion ?? record.results);
  if (!title && !overview && discussions.length === 0 && conclusions.length === 0) return null;
  return { title, overview, discussions, conclusions };
}
function readDone(record) {
  return record.done === true;
}
function normalizeActionItem(item) {
  if (typeof item === "string") {
    const content2 = item.trim();
    return content2 ? { content: content2, assignee: "", deadline: "", done: false } : null;
  }
  if (!item || typeof item !== "object") return null;
  const record = item;
  const content = readString(record, "content", "text", "action", "task");
  if (!content) return null;
  const issueType = readString(record, "issue_type", "issueType");
  const rawAssigneeId = record.assignee_id !== void 0 ? record.assignee_id : record.assigneeId;
  let assigneeId;
  if (typeof rawAssigneeId === "number" && Number.isFinite(rawAssigneeId)) {
    assigneeId = rawAssigneeId;
  } else if (rawAssigneeId === null) {
    assigneeId = null;
  }
  return {
    content,
    assignee: readString(record, "assignee", "owner", "responsible"),
    deadline: readString(record, "deadline", "due_date", "dueDate", "due"),
    done: readDone(record),
    // Omitted rather than stored blank, so "never chosen" stays one state.
    ...issueType ? { issue_type: issueType } : {},
    ...assigneeId !== void 0 ? { assignee_id: assigneeId } : {}
  };
}
function normalizeMeetingMinutesResponse(data) {
  if (!data || typeof data !== "object") return EMPTY_MEETING_MINUTES;
  const root = data;
  const nested = root.data && typeof root.data === "object" ? root.data : root.meeting_minutes && typeof root.meeting_minutes === "object" ? root.meeting_minutes : root;
  const purpose = toStringList(nested.purpose ?? nested.purposes ?? nested.goals);
  const topicsRaw = Array.isArray(nested.topics) ? nested.topics : [];
  const topics = topicsRaw.map((item) => normalizeTopic(item)).filter((item) => item !== null);
  const actionRaw = Array.isArray(nested.action_items) ? nested.action_items : Array.isArray(nested.actions) ? nested.actions : [];
  const action_items = actionRaw.map((item) => normalizeActionItem(item)).filter((item) => item !== null);
  return { purpose, topics, action_items };
}

// src/utils/meeting-brief.ts
var TICKET_GROUPS = /* @__PURE__ */ new Set(["done", "overdue", "upcoming"]);
var EMPTY_AGENDA = {
  progress: [],
  decisions: [],
  questions: [],
  risks: []
};
var TICKET_REASONS = /* @__PURE__ */ new Set([
  "backlog_not_connected",
  "backlog_no_projects",
  "backlog_scope_failed",
  "backlog_unavailable"
]);
function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function asString(value) {
  return typeof value === "string" ? value : "";
}
function asNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function parseMeeting(value) {
  const row = asRecord(value);
  if (!row) return null;
  const meeting_id = asString(row.meeting_id).trim();
  if (!meeting_id) return null;
  const ownerRaw = row.owner_user_id;
  const owner_user_id = typeof ownerRaw === "string" && ownerRaw.trim() ? ownerRaw.trim() : null;
  return {
    meeting_id,
    title: asString(row.title),
    held_at: asString(row.held_at),
    duration_seconds: asNumber(row.duration_seconds),
    ...owner_user_id ? { owner_user_id } : {}
  };
}
function parseAssignedItem(value) {
  const row = asRecord(value);
  if (!row) return null;
  const content = asString(row.content).trim();
  if (!content) return null;
  const issueRaw = row.issue_key;
  return {
    content,
    assignee: asString(row.assignee),
    deadline: asString(row.deadline),
    issue_key: typeof issueRaw === "string" && issueRaw.trim() ? issueRaw.trim() : null
  };
}
function parseAssigned(value) {
  const row = asRecord(value);
  const items = Array.isArray(row?.items) ? row.items.flatMap((item) => {
    const parsed = parseAssignedItem(item);
    return parsed ? [parsed] : [];
  }) : [];
  return {
    total: asNumber(row?.total) || items.length,
    with_ticket: asNumber(row?.with_ticket),
    items
  };
}
function parseTicket(value) {
  const row = asRecord(value);
  if (!row) return null;
  const issue_key = asString(row.issue_key).trim();
  const group = asString(row.group);
  if (!issue_key || !TICKET_GROUPS.has(group)) return null;
  const dueRaw = row.due_date;
  return {
    issue_key,
    title: asString(row.title),
    status: asString(row.status),
    group,
    assignee: asString(row.assignee),
    due_date: typeof dueRaw === "string" && dueRaw.trim() ? dueRaw : null,
    url: asString(row.url)
  };
}
function parseComment(value) {
  const row = asRecord(value);
  if (!row) return null;
  const issue_key = asString(row.issue_key).trim();
  if (!issue_key) return null;
  return {
    issue_key,
    summary: asString(row.summary),
    count: asNumber(row.count),
    authors: Array.isArray(row.authors) ? row.authors.filter((name) => typeof name === "string" && name.trim() !== "") : [],
    last_at: asString(row.last_at)
  };
}
function parseReason(value) {
  if (value == null) return null;
  if (typeof value !== "string") return null;
  return TICKET_REASONS.has(value) ? value : null;
}
function parseStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "string") return [];
    const trimmed = item.trim();
    return trimmed ? [trimmed] : [];
  });
}
function parseAgenda(value) {
  const row = asRecord(value);
  if (!row) return { ...EMPTY_AGENDA };
  return {
    progress: parseStringList(row.progress),
    decisions: parseStringList(row.decisions),
    questions: parseStringList(row.questions),
    risks: parseStringList(row.risks)
  };
}
function parseMeetingBriefPayload(raw) {
  const body = asRecord(raw);
  if (!body) return null;
  if (typeof body.generated_at !== "string" || typeof body.language !== "string") return null;
  if (!Array.isArray(body.meetings) || !Array.isArray(body.agreed)) return null;
  if (!Array.isArray(body.tickets) || !Array.isArray(body.comments)) return null;
  if (typeof body.degraded !== "boolean") return null;
  const meetings = body.meetings.flatMap((item) => {
    const parsed = parseMeeting(item);
    return parsed ? [parsed] : [];
  });
  return {
    generated_at: body.generated_at,
    language: body.language,
    meetings,
    summary: asString(body.summary),
    agreed: body.agreed.filter((item) => typeof item === "string" && item.trim() !== ""),
    assigned: parseAssigned(body.assigned),
    tickets: body.tickets.flatMap((item) => {
      const parsed = parseTicket(item);
      return parsed ? [parsed] : [];
    }),
    comments: body.comments.flatMap((item) => {
      const parsed = parseComment(item);
      return parsed ? [parsed] : [];
    }),
    tickets_unavailable_reason: parseReason(body.tickets_unavailable_reason),
    degraded: body.degraded,
    agenda: parseAgenda(body.agenda)
  };
}
function normalizeMeetingBriefProjectKey(value) {
  return value.trim().toUpperCase();
}
function parseMeetingBriefBatchResponse(raw) {
  const body = asRecord(raw);
  if (!body || !Array.isArray(body.briefs)) return null;
  const seen = /* @__PURE__ */ new Set();
  const briefs = [];
  for (const item of body.briefs) {
    const row = asRecord(item);
    if (!row) continue;
    const event_id = asString(row.event_id).trim();
    const project_key = normalizeMeetingBriefProjectKey(asString(row.project_key));
    if (!event_id || !project_key) continue;
    if (seen.has(event_id)) continue;
    const brief = parseMeetingBriefPayload(row.brief);
    if (!brief) continue;
    seen.add(event_id);
    briefs.push({ project_key, event_id, brief });
  }
  return { briefs };
}

// src/utils/event-project.ts
function asRecord2(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function parseDetectProjectResponse(raw) {
  const body = asRecord2(raw);
  if (!body) return null;
  const value = body.project_key;
  if (value !== null && typeof value !== "string") return null;
  const key = typeof value === "string" ? normalizeMeetingBriefProjectKey(value) : "";
  return { project_key: key || null };
}
export {
  EMPTY_MEETING_MINUTES,
  EMPTY_PREVIOUS_ANALYSIS,
  GLOSSARY_CSV_HEADERS,
  GLOSSARY_UPLOAD_MAX_BYTES,
  GLOSSARY_UPLOAD_MAX_CELL_LENGTH,
  GLOSSARY_UPLOAD_MAX_ROWS,
  INTERNAL_EMAIL_DOMAINS,
  LANGUAGES,
  MEETING_CONTEXT,
  MINUTES_EMPTY_TRANSCRIPT,
  NATIVE_SESSION_STATUSES,
  NATIVE_TRANSCRIPTION_PROVIDERS,
  ROOM_EVENT_BATCH_LIMIT,
  ROOM_EVENT_TYPES,
  SYSTEM_IT_DICTIONARY_NAME,
  USER_ACTIVITY_ACTIONS,
  USER_ACTIVITY_GROUP_LABELS,
  USER_ACTIVITY_INFO,
  getSystemItGlossaryTerms,
  isInternalEmail,
  isMinutesJobSettled,
  isRoomEventType,
  normalizeEmail,
  normalizeMeetingBriefProjectKey,
  normalizeMeetingMinutesResponse,
  parseDetectProjectResponse,
  parseMeetingBriefBatchResponse,
  parseMeetingBriefPayload,
  userActivityGroup
};
